import {type ReactNode, useEffect} from 'react';
import {
  FaBook,
  FaBookOpen,
  FaBug,
  FaBullhorn,
  FaChalkboardTeacher,
  FaCheck,
  FaCheckCircle,
  FaClipboardList,
  FaCode,
  FaCommentDots,
  FaComments,
  FaCompass,
  FaFeatherAlt,
  FaFlagCheckered,
  FaGlobe,
  FaGraduationCap,
  FaHeart,
  FaLanguage,
  FaLeaf,
  FaLightbulb,
  FaLock,
  FaMapSigns,
  FaPen,
  FaPencilAlt,
  FaPlay,
  FaPlayCircle,
  FaPython,
  FaQuestionCircle,
  FaRocket,
  FaSeedling,
  FaSignature,
  FaSmile,
  FaStar,
  FaTree,
  FaTrophy,
  FaUsers,
  FaVideo,
} from 'react-icons/fa';
import {GiAirBalloon, GiRingedPlanet} from 'react-icons/gi';
import styled from 'styled-components';
import {useParams} from 'react-router-dom';
import {HistoryLink} from 'hooks/router/HistoryLink';
import {
  type AllApplicationData,
  getTodoItemRoute,
  useIsApplyShortLinkRoute,
} from 'course/application/general/utils/apputils';
import {checkComplete} from 'course/application/general/utils/checkStatus';
import {useIsMobile} from 'utils/general';
import {useUserId} from 'hooks/user/useUserId';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {
  CIPX_SECTION_TEACH_SCENARIO_ID,
  SECTION_TEACH_MAX_ATTEMPTS,
} from 'course/application/sectionLeaderApplication/sectionteaching/sectionTeachingFlow';
import {useSectionTeachingAttemptSummary} from 'course/application/sectionLeaderApplication/sectionteaching/firebase/useSectionTeachingAttemptSummary';
import type {
  ApplicationTodoItem,
  ApplicationType,
  SectionLeadApplicationData,
  SectionLeadTodoItem,
  StudentApplicationData,
  TodoIconOverride,
  TodoType,
} from 'course/application/firebase/models/applicationData';

/** Badge colour for an unlocked timeline item that is not complete yet. Also
 *  used by the splash-cards editor ({@link SplashTodoEditor}) so its preview
 *  badges match this exactly. */
export const BLUE = '#5aa0e6';
/** Badge colour for a completed timeline item. Also used by the submitted
 *  banner and modal, so their completion mark matches these checks exactly. */
export const GREEN = '#74c043';
/** Connecting-line colour for an incomplete pair. Also reused by the
 *  splash-cards editor for the same reason as {@link BLUE}. */
export const LINE_GREY = '#c4d7ea';
const LOCK_GREY = '#adb5bd';

/**
 * Icon components for each optional per-item `icon` override (see
 * `TodoIconOverride`). The application editor's icon picker offers exactly
 * this set, so overriding a todo's icon never depends on a free-text name —
 * only a key that's guaranteed to resolve to a real icon.
 */
export const TODO_ICON_OVERRIDE_ICONS: Record<TodoIconOverride, ReactNode> = {
  play: <FaPlay />,
  playCircle: <FaPlayCircle />,
  video: <FaVideo />,
  book: <FaBook />,
  bookOpen: <FaBookOpen />,
  graduationCap: <FaGraduationCap />,
  lightbulb: <FaLightbulb />,
  compass: <FaCompass />,
  mapSigns: <FaMapSigns />,
  comments: <FaComments />,
  commentDots: <FaCommentDots />,
  users: <FaUsers />,
  bullhorn: <FaBullhorn />,
  rocket: <FaRocket />,
  pen: <FaPen />,
  pencilAlt: <FaPencilAlt />,
  chalkboardTeacher: <FaChalkboardTeacher />,
  bug: <FaBug />,
  code: <FaCode />,
  python: <FaPython />,
  flagCheckered: <FaFlagCheckered />,
  star: <FaStar />,
  trophy: <FaTrophy />,
  checkCircle: <FaCheckCircle />,
  clipboardList: <FaClipboardList />,
  questionCircle: <FaQuestionCircle />,
  heart: <FaHeart />,
  globe: <FaGlobe />,
  signature: <FaSignature />,
  smile: <FaSmile />,
  feather: <FaFeatherAlt />,
  seedling: <FaSeedling />,
  leaf: <FaLeaf />,
  tree: <FaTree />,
  planet: <GiRingedPlanet />,
  language: <FaLanguage />,
};

/**
 * Human-readable labels for `TODO_ICON_OVERRIDE_ICONS`, shown in the
 * application editor's icon picker.
 */
export const TODO_ICON_OVERRIDE_LABELS: Record<TodoIconOverride, string> = {
  play: 'Play (no circle)',
  playCircle: 'Play (circle)',
  video: 'Video',
  book: 'Book',
  bookOpen: 'Open book',
  graduationCap: 'Graduation cap',
  lightbulb: 'Lightbulb',
  compass: 'Compass',
  mapSigns: 'Signpost',
  comments: 'Comments',
  commentDots: 'Comment (typing)',
  users: 'Group of people',
  bullhorn: 'Bullhorn',
  rocket: 'Rocket',
  pen: 'Pen',
  pencilAlt: 'Pencil',
  chalkboardTeacher: 'Chalkboard teacher',
  bug: 'Bug',
  code: 'Code',
  python: 'Python',
  flagCheckered: 'Checkered flag',
  star: 'Star',
  trophy: 'Trophy',
  checkCircle: 'Check (circle)',
  clipboardList: 'Clipboard',
  questionCircle: 'Question mark',
  heart: 'Heart',
  globe: 'Globe',
  signature: 'Signature',
  smile: 'Smile',
  feather: 'Feather',
  seedling: 'Seedling',
  leaf: 'Leaf',
  tree: 'Tree',
  planet: 'Ringed planet',
  language: 'Language',
};

/**
 * Returns the badge icon for a todo item. Lessons and recorded demos are
 * videos, practice problems and intros are rockets, the form is a pen,
 * debugging is a bug, section teaching is a rocket, sim teaching is a
 * chalkboard, and the creative project is a hot-air balloon. An item-level
 * `icon` override (see `TodoIconOverride`) takes precedence over all of this,
 * so a single course can swap the icon for one item without touching any
 * other course's items of the same type.
 *
 * Covers both student and section-leader todo types so the CIPX timeline can
 * render either application. Exported so the splash-cards editor
 * ({@link SplashTodoEditor}) can render the exact same icon per row, keeping
 * the editor a true WYSIWYG preview of the live timeline.
 *
 * @param item - The todo item to pick an icon for.
 * @return The react-icons node to render inside the badge.
 */
export function getCipxIcon(item: ApplicationTodoItem): ReactNode {
  if (item.icon) {
    return TODO_ICON_OVERRIDE_ICONS[item.icon];
  }
  // 'practice-4' is the creative-project practice problem; it gets a balloon
  // instead of a rocket. The Game Icons balloon has more internal padding than
  // the Font Awesome icons, so bump its size to match their visual weight.
  if (item.id === 'practice-4') {
    return <GiAirBalloon size="1.3em" />;
  }
  switch (item.type) {
    case 'form':
      return <FaPen />;
    case 'code':
    case 'aboutJob':
      return <FaRocket />;
    case 'debugging':
      return <FaBug />;
    case 'sectionteaching':
    case 'sectionteaching-pai':
      return <FaRocket />;
    case 'simteaching':
      return <FaChalkboardTeacher />;
    case 'record':
    case 'lesson':
    case 'aboutCourse':
    default:
      return <FaVideo />;
  }
}

interface CipxTodoListProps {
  /** The splash data whose cards are flattened into one list. */
  applicationData: StudentApplicationData | SectionLeadApplicationData;
  /** Applicant progress, used to decide which items are complete. */
  allAppData: AllApplicationData;
  /**
   * Whether this is a student or section-leader application. Selects the splash
   * route base when navigating from a badge label.
   */
  applicationType: ApplicationType;
  /**
   * Todo types to omit from the timeline, e.g. an item surfaced separately as
   * its own card (see {@link OptionalTeachingPracticeCard}). Omitted or empty →
   * every item is shown.
   */
  excludeTypes?: readonly TodoType[];
  /**
   * Optional content rendered inside the card, above the timeline (e.g. a
   * "Welcome {name}" greeting). Omitted → the card opens straight into the
   * list.
   */
  heading?: ReactNode;
  /**
   * When true, item titles render as plain text instead of links, e.g. once
   * the application has closed or while staff preview the page. Omitted →
   * items link to their steps.
   */
  isReadOnly?: boolean;
}

/**
 * Timeline splash list: a single vertical timeline of circular icon badges. A
 * completed item's badge turns green and gains a check badge (shape signals
 * completion for colour-blind readers); the connecting line fills green between
 * adjacent completed items. Completion is driven by {@link checkComplete}.
 *
 * Renders for any course whose `ApplicationData` doc matches the `splashCards`
 * shape (see `isSectionLeadApplicationData`/`isStudentApplicationData`), not
 * only `cipx` — e.g. PAI-parented courses render here too.
 *
 * @param props - See {@link CipxTodoListProps}.
 * @return The rendered timeline.
 */
/**
 * Resolves a checklist item's destination, overriding the shared resolver only
 * for CIPX's own `sectionteaching` item: CIPX runs the SLIDES-based
 * practice-teaching exercise, while the shared resolver's `sectionteaching`
 * case resolves the IDE-based exercise every other non-PAI course uses.
 * PAI-parented courses use the distinct `sectionteaching-pai` type instead
 * (see {@link getTodoItemRoute}), which the shared resolver already routes
 * correctly, so this function passes it through unmodified.
 *
 * The parameter type is derived from {@link getTodoItemRoute} so the two stay in
 * lockstep.
 *
 * @param args - Exactly what {@link getTodoItemRoute} takes.
 * @return The route to navigate to, or `null` when the item resolves to none.
 * @throws Never.
 */
function cipxSectionTeachingRoute(
  args: Parameters<typeof getTodoItemRoute>[0],
): string | null {
  const shared = getTodoItemRoute(args);
  if (args.item.type !== 'sectionteaching' || shared === null) {
    return shared;
  }
  // Same parent-course prefix the shared resolver derives, different exercise.
  const parentPrefix = shared.split('/applyteach-sectionteach/')[0];
  return `${parentPrefix}/cipx-sectionteach/${CIPX_SECTION_TEACH_SCENARIO_ID}?apply=${args.courseId}`;
}

export const CipxTodoList = ({
  applicationData,
  allAppData,
  applicationType,
  excludeTypes,
  heading,
  isReadOnly = false,
}: CipxTodoListProps) => {
  const t = useTranslateFunction('application');
  const isMobile = useIsMobile();
  const {courseId, targetCourseId} = useParams<{
    courseId: string;
    targetCourseId: string;
  }>();
  // Preserve the URL shape the user is on (short `/apply/…` vs long form) so
  // todo links swap the layout's <Outlet/> instead of remounting the flow.
  const isShortLinkRoute = useIsApplyShortLinkRoute();

  // Widen to the todo-item union first so flattening the student- or
  // section-leader-typed cards resolves to a single callable signature.
  const items: ApplicationTodoItem[] = applicationData.splashCards
    .flatMap((card): ApplicationTodoItem[] => card.todo)
    .filter(item => !excludeTypes?.includes(item.type));
  const completeFlags = items.map(item => checkComplete(item, allAppData));

  // An item is locked when it is incomplete and its `lockedUntilComplete`
  // prerequisite is incomplete. A missing or self-referential prerequisite id
  // never locks (fail open), so a content typo can't strand an applicant.
  const isItemLocked = (
    item: ApplicationTodoItem,
    isComplete: boolean,
  ): boolean => {
    if (
      isComplete ||
      !item.lockedUntilComplete ||
      item.lockedUntilComplete === item.id
    ) {
      return false;
    }
    const prereqIndex = items.findIndex(
      it => it.id === item.lockedUntilComplete,
    );
    if (prereqIndex === -1) {
      return false;
    }
    return !completeFlags[prereqIndex];
  };

  // Preload the CIPX slides section-teaching route chunk while the checklist
  // is on screen, so clicking "Practice Teaching" skips the lazy-load fallback
  // flash. Resolves to the same chunk CourseRoutes lazy-loads, so this just
  // warms the cache. Scoped to CIPX's own `sectionteaching` type only — PAI's
  // `sectionteaching-pai` navigates to `mathteachable`, a different chunk.
  const hasSectionTeaching = items.some(
    item => item.type === 'sectionteaching',
  );
  useEffect(() => {
    if (!hasSectionTeaching) return;
    void import('course/cipxsection/CipxSectionTeachingRoute');
  }, [hasSectionTeaching]);

  return (
    <Card>
      {heading && <HeadingSlot>{heading}</HeadingSlot>}
      <List>
        {items.map((item, index) => {
          const isComplete = completeFlags[index];
          const locked = isItemLocked(item, isComplete);
          const isFirst = index === 0;
          const isLast = index === items.length - 1;
          const label =
            isMobile && item.mobileTitle ? item.mobileTitle : item.title;

          // A line half is green only when both items it joins are complete, so
          // the trail never leaks into an unfinished step.
          const trailUp = isComplete && (completeFlags[index - 1] ?? false);
          const trailDown = isComplete && (completeFlags[index + 1] ?? false);

          const badgeLabel = locked
            ? t('Task locked', {forceString: true})
            : isComplete
              ? t('Task complete!', {forceString: true})
              : t('Task incomplete!', {forceString: true});

          // Overrides CIPX's own `sectionteaching` item to its slides flow
          // (see cipxSectionTeachingRoute); passes every other type, including
          // PAI's `sectionteaching-pai`, straight through unmodified.
          const dest = cipxSectionTeachingRoute({
            item,
            courseId: targetCourseId,
            applicationType,
            parentCourseId: courseId,
            preferShortLink: isShortLinkRoute,
          });

          return (
            <Row key={item.id}>
              <BadgeCol>
                {!isFirst && <LineTop $green={trailUp} />}
                {!isLast && <LineBottom $green={trailDown} />}
                <Badge
                  $green={isComplete}
                  $locked={locked}
                  role="img"
                  aria-label={badgeLabel}
                >
                  {getCipxIcon(item)}
                  {isComplete && (
                    <CheckBadge aria-hidden="true">
                      <FaCheck />
                    </CheckBadge>
                  )}
                  {locked && (
                    <LockBadge aria-hidden="true">
                      <FaLock />
                    </LockBadge>
                  )}
                </Badge>
              </BadgeCol>
              <LabelCol>
                {isReadOnly ? (
                  <StaticLabel>{label}</StaticLabel>
                ) : locked || !dest ? (
                  <LockedLabel aria-disabled="true">{label}</LockedLabel>
                ) : (
                  <Label to={dest}>{label}</Label>
                )}
                {item.type === 'sectionteaching' && isComplete && (
                  <SectionAttemptCounter targetCourseId={targetCourseId} />
                )}
                {locked && !isReadOnly && (
                  <LockHint>{t('Complete the previous step to unlock.')}</LockHint>
                )}
              </LabelCol>
            </Row>
          );
        })}
      </List>
    </Card>
  );
};

/**
 * "You can try again if you wish" subtitle for the section-teaching item, shown
 * only once the item is complete (the caller gates on completion). Framed around
 * the *optional* retries still available rather than a running "N used" tally, so
 * a finished applicant isn't nudged into burning attempts they don't need.
 *
 * Used attempts are `summary.attemptCount` (the server cap counter, which
 * counts takes that actually happened, not Start clicks); {max} is
 * `summary.maxAttempts`, falling back to {@link SECTION_TEACH_MAX_ATTEMPTS}
 * before the summary loads. Its own component so the hook runs unconditionally.
 */
function SectionAttemptCounter({targetCourseId}: {targetCourseId: string}) {
  const t = useTranslateFunction('application');
  const userId = useUserId();
  const {summary} = useSectionTeachingAttemptSummary(userId, targetCourseId);
  const used = summary?.attemptCount ?? 0;
  const max = summary?.maxAttempts ?? SECTION_TEACH_MAX_ATTEMPTS;
  const remaining = Math.max(0, max - used);
  return (
    <AttemptCount>
      {remaining > 0
        ? t('You can try again if you wish ({{remaining}} left).', {
            substitutions: {remaining: String(remaining)},
          })
        : t("You've used all {{max}} of your attempts.", {
            substitutions: {max: String(max)},
          })}
    </AttemptCount>
  );
}

interface OptionalTeachingPracticeCardProps {
  /** The section-leader splash data to pull the optional 1:1 item from. */
  applicationData: SectionLeadApplicationData;
  /** Applicant progress, used to show the item's completion state. */
  allAppData: AllApplicationData;
  /**
   * When true, the title renders as plain text instead of a link. Omitted →
   * the title links to the practice.
   */
  isReadOnly?: boolean;
}

/**
 * Standalone card for the optional 1:1 teaching-practice step (the `simteaching`
 * item), shown beneath the main timeline rather than inline. Renders nothing
 * when the application has no `simteaching` item.
 *
 * @param props - See {@link OptionalTeachingPracticeCardProps}.
 * @return The rendered card, or `null` when there is no 1:1 practice item.
 */
export const OptionalTeachingPracticeCard = ({
  applicationData,
  allAppData,
  isReadOnly = false,
}: OptionalTeachingPracticeCardProps) => {
  const t = useTranslateFunction('application');
  const {courseId, targetCourseId} = useParams<{
    courseId: string;
    targetCourseId: string;
  }>();
  // Preserve the URL shape the user is on (short `/apply/…` vs long form) so
  // this card's link swaps the layout's <Outlet/> instead of remounting.
  const isShortLinkRoute = useIsApplyShortLinkRoute();

  const item = applicationData.splashCards
    .flatMap((card): SectionLeadTodoItem[] => card.todo)
    .find(todo => todo.type === 'simteaching');
  if (!item) {
    return null;
  }

  const isComplete = checkComplete(item, allAppData);
  const route = cipxSectionTeachingRoute({
    item,
    courseId: targetCourseId,
    applicationType: 'sl',
    parentCourseId: courseId,
    preferShortLink: isShortLinkRoute,
  });

  return (
    <Card>
      <OptionalLabel>{t('Optional')}</OptionalLabel>
      <Row>
        <BadgeCol>
          <Badge
            $green={isComplete}
            role="img"
            aria-label={
              isComplete
                ? t('Task complete!', {forceString: true})
                : t('Task incomplete!', {forceString: true})
            }
          >
            {getCipxIcon(item)}
            {isComplete && (
              <CheckBadge aria-hidden="true">
                <FaCheck />
              </CheckBadge>
            )}
          </Badge>
        </BadgeCol>
        <LabelCol>
          {isReadOnly ? (
            <StaticLabel>{t('More Teaching Practice')}</StaticLabel>
          ) : route ? (
            <Label to={route}>{t('More Teaching Practice')}</Label>
          ) : (
            <LockedLabel aria-disabled="true">
              {t('More Teaching Practice')}
            </LockedLabel>
          )}
        </LabelCol>
      </Row>
    </Card>
  );
};

const Card = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-top: 12px;

  @media (max-width: 768px) {
    border-radius: 0;
    /* Left stays 0 — the badges self-inset ~8px by centering in their wider
       column — so add 8px only on the right to keep labels symmetric with it. */
    padding: 16px 8px 16px 0;
  }
`;

// Wraps the optional heading (e.g. the welcome greeting) rendered above the
// timeline. On mobile the Card's left padding is 0 (see Card), so inset the
// heading by the same 8px the header's card-body uses — keeping it flush with
// the "{Role} Application" title and the badge circles, which self-inset 8px
// by centering in their wider column.
const HeadingSlot = styled.div`
  @media (max-width: 768px) {
    padding-left: 8px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  min-height: 78px;
`;

const BadgeCol = styled.div`
  position: relative;
  width: 64px;
  min-width: 64px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const lineBase = `
  position: absolute;
  left: 50%;
  width: 4px;
  transform: translateX(-50%);
`;

const LineTop = styled.div<{$green: boolean}>`
  ${lineBase}
  top: 0;
  height: 50%;
  background: ${p => (p.$green ? GREEN : LINE_GREY)};
`;

const LineBottom = styled.div<{$green: boolean}>`
  ${lineBase}
  top: 50%;
  height: 50%;
  background: ${p => (p.$green ? GREEN : LINE_GREY)};
`;

const Badge = styled.div<{$green: boolean; $locked?: boolean}>`
  position: relative;
  z-index: 1;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  background: ${p => (p.$locked ? LOCK_GREY : p.$green ? GREEN : BLUE)};
`;

// White lock badge on a locked item's badge — signals "locked" by shape as well
// as colour, for colour-blind readers.
const LockBadge = styled.span`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  color: ${LOCK_GREY};
  border: 2px solid ${LOCK_GREY};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
`;

// White check badge on a completed item's badge — signals completion by shape,
// not colour alone, for colour-blind readers.
const CheckBadge = styled.span`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  color: ${GREEN};
  border: 2px solid ${GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const LabelCol = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
`;

const Label = styled(HistoryLink)`
  display: block;
  text-align: left;
  text-decoration: none;
  font-size: 1.3em;
  font-weight: 500;
  color: #337ab7;
  cursor: pointer;
`;

const StaticLabel = styled.span`
  font-size: 1.3em;
  font-weight: 500;
  color: #495057;
  cursor: default;
`;

const LockedLabel = styled.span`
  font-size: 1.3em;
  font-weight: 500;
  color: #9aa4ad;
  cursor: default;
`;

const LockHint = styled.span`
  font-size: 0.85em;
  color: #9aa4ad;
  margin-top: 2px;
`;

const AttemptCount = styled.span`
  font-size: 0.85em;
  color: #6c757d;
  margin-top: 2px;
`;

const OptionalLabel = styled.div`
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin-bottom: 4px;
`;
